import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  collection, 
  query, 
  orderBy, 
  limit,
  getDocs,
  startAfter
} from "firebase/firestore";
import { db } from "./firebase";
import { useAuth } from "./AuthContext";
import "./styles.css";
import "./auth-management.css";

export default function AuditLog() {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [userFilter, setUserFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [lastVisible, setLastVisible] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const LOGS_PER_PAGE = 50;

  // Fetch audit logs
  const fetchLogs = async (loadMore = false) => {
    try {
      setLoading(true);
      
      let q = query(
        collection(db, "auditLogs"),
        orderBy("timestamp", "desc"),
        limit(LOGS_PER_PAGE)
      );

      // If loading more, start after last document
      if (loadMore && lastVisible) {
        q = query(
          collection(db, "auditLogs"),
          orderBy("timestamp", "desc"),
          startAfter(lastVisible),
          limit(LOGS_PER_PAGE)
        );
      }

      const querySnapshot = await getDocs(q);
      const logsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      if (loadMore) {
        setLogs(prev => [...prev, ...logsData]);
      } else {
        setLogs(logsData);
      }

      // Set last visible for pagination
      setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]);
      setHasMore(querySnapshot.docs.length === LOGS_PER_PAGE);
    } catch (error) {
      console.error("Error fetching audit logs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  // Filter logs client-side
  const filteredLogs = logs.filter((log) => {
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = 
        log.userEmail?.toLowerCase().includes(query) ||
        log.userDisplayName?.toLowerCase().includes(query) ||
        log.action?.toLowerCase().includes(query) ||
        log.targetName?.toLowerCase().includes(query) ||
        log.details?.toLowerCase().includes(query);
      
      if (!matchesSearch) return false;
    }

    // Action filter
    if (actionFilter !== "all" && log.action !== actionFilter) {
      return false;
    }

    // User filter
    if (userFilter && log.userEmail !== userFilter) {
      return false;
    }

    // Date filter
    if (dateFilter !== "all" && log.timestamp) {
      const logDate = log.timestamp.toDate();
      const now = new Date();
      const daysDiff = Math.floor((now - logDate) / (1000 * 60 * 60 * 24));
      
      if (dateFilter === "today" && daysDiff > 0) return false;
      if (dateFilter === "week" && daysDiff > 7) return false;
      if (dateFilter === "month" && daysDiff > 30) return false;
    }

    return true;
  });

  // Get unique users for filter dropdown
  const uniqueUsers = [...new Set(logs.map(log => log.userEmail))].filter(Boolean).sort();

  // Get unique actions for filter dropdown
  const uniqueActions = [...new Set(logs.map(log => log.action))].filter(Boolean).sort();

  // Format action for display
  const formatAction = (action) => {
    return action?.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ') || 'Unknown';
  };

  // Get icon for action
  const getActionIcon = (action) => {
    if (action?.includes('login')) return '🔐';
    if (action?.includes('logout')) return '🚪';
    if (action?.includes('register')) return '📝';
    if (action?.includes('create')) return '➕';
    if (action?.includes('edit')) return '✏️';
    if (action?.includes('delete')) return '🗑️';
    if (action?.includes('archive')) return '📦';
    if (action?.includes('unarchive')) return '📂';
    if (action?.includes('status')) return '🔄';
    if (action?.includes('role')) return '👤';
    return '📋';
  };

  // Check if current user has admin access
  const isAdmin = userProfile?.role === "admin";
  const isManager = userProfile?.role === "manager" || isAdmin;

  if (!isAdmin && !isManager) {
    return (
      <div className="management-container">
        <div className="management-header">
          <button className="back-btn" onClick={() => navigate("/dashboard")}>
            ← Back to Dashboard
          </button>
          <h1>🔒 Access Denied</h1>
          <p>You don't have permission to view audit logs.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="management-container">
      <div className="management-header">
        <button className="back-btn" onClick={() => navigate("/dashboard")}>
          ← Back to Dashboard
        </button>
        <h1>📜 Audit Log</h1>
        <p>Track all user actions and changes</p>
      </div>

      <div className="management-toolbar" style={{ marginBottom: '20px' }}>
        <input
          type="text"
          className="search-input"
          placeholder="🔍 Search logs..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ flex: '1', marginRight: '10px' }}
        />
        
        <select 
          className="filter-select"
          value={actionFilter}
          onChange={(e) => setActionFilter(e.target.value)}
          style={{ marginRight: '10px', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
        >
          <option value="all">All Actions</option>
          {uniqueActions.map(action => (
            <option key={action} value={action}>{formatAction(action)}</option>
          ))}
        </select>

        <select 
          className="filter-select"
          value={userFilter}
          onChange={(e) => setUserFilter(e.target.value)}
          style={{ marginRight: '10px', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
        >
          <option value="">All Users</option>
          {uniqueUsers.map(user => (
            <option key={user} value={user}>{user}</option>
          ))}
        </select>

        <select 
          className="filter-select"
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
        >
          <option value="all">All Time</option>
          <option value="today">Today</option>
          <option value="week">Last 7 Days</option>
          <option value="month">Last 30 Days</option>
        </select>
      </div>

      <div className="log-count" style={{ marginBottom: '10px', color: '#666' }}>
        Showing {filteredLogs.length} log{filteredLogs.length !== 1 ? 's' : ''}
      </div>

      {loading && logs.length === 0 ? (
        <div className="loading-spinner">Loading audit logs...</div>
      ) : (
        <>
          <div className="audit-log-container" style={{ 
            background: 'white', 
            borderRadius: '8px', 
            padding: '20px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            {filteredLogs.length === 0 ? (
              <div className="empty-table-message">
                {searchQuery || actionFilter !== "all" || userFilter || dateFilter !== "all"
                  ? "No logs match your filters"
                  : "No audit logs found"}
              </div>
            ) : (
              <div className="audit-log-list">
                {filteredLogs.map((log) => (
                  <div key={log.id} className="audit-log-entry" style={{
                    borderBottom: '1px solid #eee',
                    padding: '15px 0',
                    display: 'flex',
                    gap: '15px'
                  }}>
                    <div style={{ fontSize: '24px', flexShrink: 0 }}>
                      {getActionIcon(log.action)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '5px' }}>
                        <div>
                          <strong>{formatAction(log.action)}</strong>
                          <span style={{ color: '#666', marginLeft: '10px' }}>
                            by {log.userDisplayName || log.userEmail}
                          </span>
                        </div>
                        <span style={{ color: '#999', fontSize: '0.9em', whiteSpace: 'nowrap', marginLeft: '10px' }}>
                          {log.timestamp?.toDate().toLocaleString() || 'Unknown time'}
                        </span>
                      </div>
                      
                      {log.targetName && (
                        <div style={{ color: '#666', fontSize: '0.95em', marginBottom: '3px' }}>
                          Target: <strong>{log.targetName}</strong>
                          {log.targetType && ` (${log.targetType})`}
                        </div>
                      )}
                      
                      {log.changes && (
                        <div style={{ 
                          background: '#f5f5f5', 
                          padding: '8px 12px', 
                          borderRadius: '4px', 
                          fontSize: '0.9em',
                          marginTop: '5px'
                        }}>
                          <strong>Changes:</strong>{' '}
                          {log.changes.from && log.changes.to 
                            ? `"${log.changes.from}" → "${log.changes.to}"`
                            : JSON.stringify(log.changes)}
                        </div>
                      )}
                      
                      {log.details && (
                        <div style={{ color: '#666', fontSize: '0.9em', marginTop: '5px', fontStyle: 'italic' }}>
                          {log.details}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {hasMore && !loading && (
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <button 
                className="new-btn"
                onClick={() => fetchLogs(true)}
                style={{ padding: '10px 20px' }}
              >
                Load More
              </button>
            </div>
          )}

          {loading && logs.length > 0 && (
            <div style={{ textAlign: 'center', marginTop: '20px', color: '#666' }}>
              Loading more logs...
            </div>
          )}
        </>
      )}
    </div>
  );
}
